/**
 * Every register filter a campaign can target on, as a form.
 *
 * Same fields, same names and same `ariregister.filter.*` labels as the company
 * list page - deliberately. If "Harju maakond + has e-mail" selects 12 000 rows
 * on one screen it has to mean the same 12 000 on the other, and the surest way
 * to keep that true is to render the same filter object through the same
 * labels. The server half is `entityFilterSchema` / `buildEntityWhere`.
 *
 * Four things this form says out loud, because the data does not mean what the
 * field names suggest:
 *
 *   - The website filter's "no" means "never told the register", not "has no
 *     website". Only a small share of the mirror ever reported one.
 *   - Sole traders are natural persons, and cold mail to one needs consent.
 *   - Three of the fields read columns that a sync dataset has to fill in, and
 *     on the production mirror it never has. `coverage` carries the real numbers
 *     and the field states them: a filter that can only return zero must say so
 *     rather than returning zero and letting the operator guess why. This is not
 *     hypothetical - it is what sent somebody looking for a bug in the filter.
 *   - Partial coverage is the same trap in slow motion. Filtering on "profit
 *     above zero" silently drops every company with no report at all, so the
 *     ratio is shown even when it is not zero.
 *
 * Layout: four filters carry most of the work and stay visible; the other ten
 * sit behind a fold that always states how many of them are set, so a filter
 * hidden by the fold is never silently applied.
 */
import { useEffect, useMemo, useState } from "react";
import { ChevronDown, Database, Search, SlidersHorizontal } from "lucide-react";
import {
  Combobox,
  DateRangePicker,
  Input,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Switch,
  cn,
} from "@wa/ui";
import { useI18n, useT } from "../../../../i18n/I18nProvider";
import { COUNTIES, LEGAL_FORMS } from "../../../../lib/ariregister";
import { EMTAK_ACTIVITY_OPTIONS } from "../../../../lib/emtak";
import {
  ANY,
  advancedFilterCount,
  draftFromAudience,
  registeredRange,
  toIsoDate,
  type AudienceDraft,
  type AudienceFilter,
} from "../../../../lib/audienceFilter";
import type { RegisterCoverage } from "../../../../lib/marketing";
import { Field, num, pct } from "../shared";

function TriSelect({
  value,
  onChange,
  yes,
  no,
  any,
  disabled,
}: {
  value: string;
  onChange: (v: string) => void;
  yes: string;
  no: string;
  any: string;
  disabled?: boolean;
}) {
  return (
    <Select value={value} onValueChange={onChange} disabled={disabled}>
      <SelectTrigger className="w-full">
        <SelectValue />
      </SelectTrigger>
      <SelectContent>
        <SelectItem value={ANY}>{any}</SelectItem>
        <SelectItem value="true">{yes}</SelectItem>
        <SelectItem value="false">{no}</SelectItem>
      </SelectContent>
    </Select>
  );
}

function ListSelect({
  value,
  onChange,
  options,
  any,
  disabled,
}: {
  value: string;
  onChange: (v: string) => void;
  options: { v: string; label: string }[];
  any: string;
  disabled?: boolean;
}) {
  return (
    <Select value={value} onValueChange={onChange} disabled={disabled}>
      <SelectTrigger className="w-full">
        <SelectValue />
      </SelectTrigger>
      <SelectContent>
        <SelectItem value={ANY}>{any}</SelectItem>
        {options.map((o) => (
          <SelectItem key={o.v} value={o.v}>
            {o.label}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}

/** A pair of number inputs sharing one label. */
function RangeInputs({
  min,
  max,
  onMin,
  onMax,
  disabled,
  minLabel,
  maxLabel,
}: {
  min: string;
  max: string;
  onMin: (v: string) => void;
  onMax: (v: string) => void;
  disabled?: boolean;
  minLabel: string;
  maxLabel: string;
}) {
  return (
    <div className="flex items-center gap-2">
      <Input
        value={min}
        onChange={(e) => onMin(e.target.value)}
        placeholder={minLabel}
        inputMode="numeric"
        disabled={disabled}
      />
      <span className="text-xs text-muted-foreground">-</span>
      <Input
        value={max}
        onChange={(e) => onMax(e.target.value)}
        placeholder={maxLabel}
        inputMode="numeric"
        disabled={disabled}
      />
    </div>
  );
}

/** How much of the mirror a field can actually see, on its own label. Absent
 *  while the numbers are still loading rather than guessing at 100%. */
function CoverageBadge({ have, total }: { have: number; total: number }) {
  const t = useT();
  const empty = have === 0;
  return (
    <span
      title={t("marketing.audience.coverageTitle", { n: num(have), total: num(total) })}
      className={cn(
        "inline-flex items-center gap-1 rounded-full border px-1.5 py-px font-mono text-[10px]",
        empty
          ? "border-amber-500/40 bg-amber-500/10 text-amber-700 dark:text-amber-400"
          : "border-border bg-muted/50 text-muted-foreground",
      )}
    >
      <Database className="size-2.5" />
      {pct(have, total)}
    </span>
  );
}

/** The sentence a zero-coverage field owes the operator. */
function EmptyColumnHint({ have, total }: { have: number; total: number }) {
  const t = useT();
  if (have > 0) return <>{t("marketing.audience.coveragePartial", { n: num(have), total: num(total) })}</>;
  return (
    <span className="text-amber-600 dark:text-amber-400">{t("marketing.audience.coverageNone")}</span>
  );
}

export function RegisterFilterForm({
  filter,
  onChange,
  disabled,
  resetKey,
  coverage,
}: {
  filter: AudienceFilter;
  onChange: (p: Partial<AudienceFilter>) => void;
  disabled?: boolean;
  /** Changes when a different filter is loaded from outside (another campaign,
   *  or "clear"). Only then are the typing buffers reseeded. */
  resetKey: string;
  /** How many rows each wealth column actually holds. Null while loading. */
  coverage: RegisterCoverage | null;
}) {
  const t = useT();
  const { locale } = useI18n();

  // Typing buffers for the free-text and amount fields. Committing on every
  // keystroke would make "2" on the way to "20" a saved audience, and the
  // preview would refire against it.
  const [draft, setDraft] = useState<AudienceDraft>(() => draftFromAudience(filter));
  const patchDraft = (p: Partial<AudienceDraft>) => setDraft((d) => ({ ...d, ...p }));

  const advanced = advancedFilterCount(filter);
  // Opened by default when something inside is already set: a fold that hides an
  // active filter is worse than no fold.
  const [open, setOpen] = useState(() => advanced > 0);

  // Reseeded on `resetKey`, NOT on every `filter` change. Watching the filter
  // would look right and lose characters: the debounce commits "20", the commit
  // changes the filter, and the reseed would overwrite the "200" typed in the
  // meantime with the value that was already in flight.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => setDraft(draftFromAudience(filter)), [resetKey]);

  useEffect(() => {
    const id = setTimeout(() => {
      onChange({
        q: draft.q.trim(),
        capitalMin: draft.capitalMin.trim(),
        capitalMax: draft.capitalMax.trim(),
        revenueMin: draft.revenueMin.trim(),
        revenueMax: draft.revenueMax.trim(),
        employeesMin: draft.employeesMin.trim(),
        employeesMax: draft.employeesMax.trim(),
      });
    }, 400);
    return () => clearTimeout(id);
  }, [draft, onChange]);

  const activityOptions = useMemo(
    () => EMTAK_ACTIVITY_OPTIONS.map((o) => ({ value: o.value, label: o.label, hint: o.hint })),
    [],
  );

  const range = registeredRange(filter);
  const total = coverage?.total ?? 0;

  return (
    <div className="space-y-4">
      {/* ── The three that do most of the work. Three columns and not four:
             at four, a county name is wider than its own select. ────────── */}
      <div className="grid grid-cols-1 gap-x-4 gap-y-3.5 sm:grid-cols-2 lg:grid-cols-3">
        <Field label={t("ariregister.filter.county")}>
          <ListSelect
            value={filter.county}
            onChange={(v) => onChange({ county: v })}
            options={COUNTIES.map((c) => ({ v: c, label: c }))}
            any={t("ariregister.filter.countyAny")}
            disabled={disabled}
          />
        </Field>

        <Field label={t("ariregister.filter.activity")}>
          <Combobox
            options={activityOptions}
            value={filter.emtak}
            onChange={(v) => onChange({ emtak: v })}
            clearable
            disabled={disabled}
            placeholder={t("ariregister.filter.activity")}
            searchPlaceholder={t("ariregister.filter.activitySearch")}
            emptyLabel={t("ariregister.filter.activityEmpty")}
            popoverClassName="w-[24rem]"
          />
        </Field>

        <Field label={t("ariregister.filter.status")}>
          <ListSelect
            value={filter.status}
            onChange={(v) => onChange({ status: v })}
            options={[
              { v: "R", label: t("ariregister.status.R") },
              { v: "L", label: t("ariregister.status.L") },
              { v: "N", label: t("ariregister.status.N") },
            ]}
            any={t("ariregister.filter.statusAny")}
            disabled={disabled}
          />
        </Field>
      </div>

      <Field label={t("marketing.audience.search")} hint={t("marketing.audience.searchHint")}>
        <div className="relative">
          <Search className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            value={draft.q}
            onChange={(e) => patchDraft({ q: e.target.value })}
            placeholder={t("marketing.audience.searchPlaceholder")}
            className="pl-9"
            disabled={disabled}
          />
        </div>
      </Field>

      {/* ── The rest, behind a fold that always declares its contents ───── */}
      <div className="rounded-xl border border-border bg-background/30">
        <button
          type="button"
          onClick={() => setOpen((v) => !v)}
          aria-expanded={open}
          className="flex w-full items-center gap-2 px-3.5 py-2.5 text-left text-xs font-medium transition-colors hover:bg-accent/40"
        >
          <SlidersHorizontal className="size-3.5 text-muted-foreground" />
          {t("marketing.audience.advanced")}
          {advanced > 0 && (
            <span className="rounded-full bg-primary/15 px-1.5 py-px font-mono text-[10px] text-primary">
              {advanced}
            </span>
          )}
          <ChevronDown
            className={cn("ml-auto size-3.5 text-muted-foreground transition-transform", open && "rotate-180")}
          />
        </button>

        {open && (
          <div className="grid grid-cols-1 gap-x-4 gap-y-3.5 border-t border-border p-3.5 sm:grid-cols-2 lg:grid-cols-3">
            <Field label={t("ariregister.filter.legalForm")} hint={t("marketing.audience.legalFormHint")}>
              <ListSelect
                value={filter.legalForm}
                onChange={(v) => onChange({ legalForm: v })}
                options={LEGAL_FORMS.map((f) => ({ v: f, label: f }))}
                any={t("ariregister.filter.legalFormAny")}
                disabled={disabled}
              />
            </Field>

            <Field label={t("ariregister.filter.vat")}>
              <TriSelect
                value={filter.hasVat}
                onChange={(v) => onChange({ hasVat: v })}
                yes={t("ariregister.filter.vatYes")}
                no={t("ariregister.filter.vatNo")}
                any={t("ariregister.filter.vatAny")}
                disabled={disabled}
              />
            </Field>

            {/* Not "has no website". Only a fraction of the register ever
                reported one, so "false" means "never told the register", which
                is a lead signal and not a fact. The hint says so on screen. */}
            <Field label={t("ariregister.filter.website")} hint={t("marketing.audience.websiteHint")}>
              <TriSelect
                value={filter.hasWebsite}
                onChange={(v) => onChange({ hasWebsite: v })}
                yes={t("ariregister.filter.websiteYes")}
                no={t("ariregister.filter.websiteNo")}
                any={t("ariregister.filter.websiteAny")}
                disabled={disabled}
              />
            </Field>

            <Field
              label={t("marketing.audience.revenue")}
              badge={coverage && <CoverageBadge have={coverage.withRevenue} total={total} />}
              hint={coverage && <EmptyColumnHint have={coverage.withRevenue} total={total} />}
            >
              <RangeInputs
                min={draft.revenueMin}
                max={draft.revenueMax}
                onMin={(v) => patchDraft({ revenueMin: v })}
                onMax={(v) => patchDraft({ revenueMax: v })}
                minLabel={t("marketing.audience.min")}
                maxLabel={t("marketing.audience.max")}
                disabled={disabled}
              />
            </Field>

            <Field
              label={t("marketing.audience.employees")}
              badge={coverage && <CoverageBadge have={coverage.withEmployees} total={total} />}
              hint={coverage && <EmptyColumnHint have={coverage.withEmployees} total={total} />}
            >
              <RangeInputs
                min={draft.employeesMin}
                max={draft.employeesMax}
                onMin={(v) => patchDraft({ employeesMin: v })}
                onMax={(v) => patchDraft({ employeesMax: v })}
                minLabel={t("marketing.audience.min")}
                maxLabel={t("marketing.audience.max")}
                disabled={disabled}
              />
            </Field>

            <Field
              label={t("marketing.audience.profitable")}
              badge={coverage && <CoverageBadge have={coverage.withReport} total={total} />}
              hint={coverage && <EmptyColumnHint have={coverage.withReport} total={total} />}
            >
              <TriSelect
                value={filter.profitable}
                onChange={(v) => onChange({ profitable: v })}
                yes={t("marketing.audience.profitableYes")}
                no={t("marketing.audience.profitableNo")}
                any={t("marketing.audience.profitableAny")}
                disabled={disabled}
              />
            </Field>

            <Field label={t("marketing.audience.capital")}>
              <RangeInputs
                min={draft.capitalMin}
                max={draft.capitalMax}
                onMin={(v) => patchDraft({ capitalMin: v })}
                onMax={(v) => patchDraft({ capitalMax: v })}
                minLabel={t("marketing.audience.min")}
                maxLabel={t("marketing.audience.max")}
                disabled={disabled}
              />
            </Field>

            <Field label={t("ariregister.filter.registered")} className="lg:col-span-2">
              {/* The picker has no `disabled` prop of its own, so a read-only
                  view blocks it at the wrapper instead of pretending it is
                  editable. The locale is passed on purpose: without it the
                  trigger falls back to its English placeholder in the middle of
                  an otherwise translated form. */}
              <div className={disabled ? "pointer-events-none opacity-50" : undefined} aria-disabled={disabled}>
                <DateRangePicker
                  value={range}
                  locale={locale}
                  placeholder={t("ariregister.filter.registeredAny")}
                  className="h-10 w-full text-sm font-normal"
                  onChange={(r) =>
                    onChange({
                      registeredFrom: r ? toIsoDate(r.from, false) : null,
                      registeredTo: r ? toIsoDate(r.to, true) : null,
                    })
                  }
                />
              </div>
            </Field>

            <div className="flex flex-col justify-end gap-2 sm:col-span-2 lg:col-span-3">
              <label className="flex flex-wrap items-center gap-2.5 text-sm">
                <Switch
                  checked={filter.hasEmail}
                  onCheckedChange={(v) => onChange({ hasEmail: v })}
                  disabled={disabled}
                />
                <span>{t("marketing.audience.hasEmail")}</span>
                <span className="text-[11px] text-muted-foreground">{t("marketing.audience.hasEmailHint")}</span>
              </label>
              <label className="flex items-center gap-2.5 text-sm">
                <Switch
                  checked={filter.hasPhone}
                  onCheckedChange={(v) => onChange({ hasPhone: v })}
                  disabled={disabled}
                />
                <span>{t("marketing.audience.hasPhone")}</span>
              </label>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
